import { useMessageContext } from '@consigli/hooks';
import { ConflictType, type Finding } from '@consigli/types';
import { useTranslation } from 'react-i18next';

import { parseMilestonePayload } from '@/pages/insight/dashboard/rfp/rfp-timeline-utils';
import { getBidGapsCustomMessage } from '@/util/bid-gaps-finding-message';
import { getPidCustomCause } from '@/util/pid-finding-message';

import { requirementPayload } from './response-requirements';

/**
 * An RFP milestone or response requirement stores an object as its message, not
 * text for the template, so its name stands in: the milestone, or the category.
 */
const getRfpCustomMessage = (finding: Finding): string | null => {
  if (finding.conflictType === ConflictType.MILESTONE) {
    return parseMilestonePayload(finding)?.milestone ?? null;
  }
  if (finding.conflictType === ConflictType.DELIVERABLE) {
    return requirementPayload(finding)?.category ?? null;
  }
  return null;
};

/**
 * The Findings page's own resolution order for a finding's headline, so a
 * finding reads the same in the add-context list, on its chip and on that page.
 * Extracted because the card and the chip both need it and the order matters:
 * the RFP, PID and bid-gaps overrides come before the plain template.
 */
export const useFindingMessage = (finding: Finding): string => {
  const { t } = useTranslation();
  const messageContext = useMessageContext(finding);

  return (
    getRfpCustomMessage(finding) ??
    getPidCustomCause(finding.messageTemplate, finding.conflictType, messageContext) ??
    getBidGapsCustomMessage(messageContext) ??
    t(finding.messageTemplate, { ...messageContext })
  );
};
